import { Route, Switch, useHistory } from 'react-router-dom';

import { useViewerRoles } from '@treams/treams-hooks';
import { Jumbo } from '@treams/treams-ui';

import Billing from '../billing/Billing';
import Connectors from '../connectors/Connectors';
import EngagementSettings from '../engagement/settings/EngagementSettings';
import FeedbackSettingsV2 from '../feedback/settings/FeedbackSettings';
import OrganizationSettings from './OrganizationSettings';
import AdvancedSettings from './advanced/AdvancedSettings';
import BulkEmployeeEdit from './bulk-employee-edit/BulkEmployeeEdit';
import CompetenciesSettings from './competencies/CompetenciesSettings';
import ConversationsSettings from './conversations/ConversationsSettings';
import CyclesSettings from './cycles/CyclesSettings';
import EmployeesSettings from './employees/EmployeesSettings';
import GoalsSettings from './goals/GoalsSettings';
import JobPositionsSettings from './jobs/JobPositionsSettings';
import JobsSettings from './jobs/JobsSettings';
import LaunchSettings from './launch/LaunchSettings';
import LearningSettings from './learning/LearningSettings';
import NotesTemplatesSettings from './notes-templates/NotesTemplatesSettings';
import NotificationsSettings from './notifications/NotificationsSettings';
import OverviewSettings from './overview/OverviewSettings';
import UserRolesSettings from './roles/UserRolesSettings';
import TalentReviewsSettings from './talent-reviews/TalentReviewsSettings';

// This is a hook that redirects to the home page if the user is not authorized to access the settings page.
function useSettingsAuthorization(): {
	loading: boolean;
	authorized?: boolean;
	isHRBusinessPartner?: boolean;
} {
	const {
		isWorkspaceSuperuser,
		isWorkspaceAdmin,
		isAdministrator,
		isEngagementOfficer,
		isHRBusinessPartner,
		loading,
	} = useViewerRoles();
	const history = useHistory();

	if (loading) {
		return { loading: true, authorized: undefined, isHRBusinessPartner: undefined };
	}

	const authorized =
		isWorkspaceSuperuser ||
		isWorkspaceAdmin ||
		isEngagementOfficer ||
		isHRBusinessPartner ||
		isAdministrator;

	if (!authorized) {
		history.push('/');
	}

	return { loading, authorized, isHRBusinessPartner };
}

export default function Settings() {
	const { loading, isHRBusinessPartner } = useSettingsAuthorization();

	if (loading) {
		return <Jumbo.Skeleton />;
	}

	return (
		<Switch>
			<Route path="/settings" exact>
				<OverviewSettings />
			</Route>

			<Route path="/settings/organization">
				<OrganizationSettings />
			</Route>
			<Route path="/settings/jobs">
				<JobsSettings />
			</Route>
			<Route path="/settings/competencies">
				<CompetenciesSettings />
			</Route>
			<Route path="/settings/goals">
				<GoalsSettings />
			</Route>
			<Route path="/settings/cycles">
				<CyclesSettings />
			</Route>
			{!isHRBusinessPartner && (
				<Route path="/settings/notifications">
					<NotificationsSettings />
				</Route>
			)}
			<Route path="/settings/billing">
				<Billing />
			</Route>
			<Route path="/settings/feedback">
				<FeedbackSettingsV2 />
			</Route>
			<Route path="/settings/conversations">
				<ConversationsSettings />
			</Route>
			<Route path="/settings/talent-reviews">
				<TalentReviewsSettings />
			</Route>
			<Route path="/settings/learning">
				<LearningSettings />
			</Route>
			<Route path="/settings/integrations">
				<Connectors />
			</Route>

			<Route path="/settings/engagement">
				<EngagementSettings />
			</Route>
			<Route path="/settings/note-templates">
				<NotesTemplatesSettings />
			</Route>
			<Route path="/settings/bulk">
				<BulkEmployeeEdit />
			</Route>
			<Route
				path={[
					'/settings/workspaces',
					'/settings/workbench',
					'/settings/modules',
					'/settings/announcements',
					'/settings/analytics',
				]}
			>
				<LaunchSettings />
			</Route>
			<Route path="/settings/employees">
				<EmployeesSettings />
			</Route>
			<Route path="/settings/roles">
				<UserRolesSettings />
			</Route>
			<Route path="/settings/job-positions">
				<JobPositionsSettings />
			</Route>
			<Route path="/settings/advanced">
				<AdvancedSettings />
			</Route>
		</Switch>
	);
}
